// -----------------------------------------------------------------------------
// src/components/ReportStatistics.jsx
// -----------------------------------------------------------------------------
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchIncidentTypeDailyThunk } from '../features/reports/reportThunks';

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ReferenceLine,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// ─────────────── styles (UNCHANGED) ───────────────
const styles = `
  .graph-container {
    background: linear-gradient(135deg,#f0effce6,#ffdaf8e6 25%,#ffdddae6 50%,#f9f3ebe6 60%,#fef3f2e6 65%,#fdf0f2e6);
    width: 100%;
    height: 255px;
    padding: 16px;
    border-radius: 8px;
    box-sizing: border-box;
    margin-top:50px;
  }
  .header-container{display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;}
  .graph-title{font-size:24px;font-weight:600;color:#1a1a1a;}
  .trend-arrow{color:#666666;}
  .tooltip-container{background:#fff;padding:16px;border-radius:6px;box-shadow:0 2px 8px rgba(0,0,0,.15);border:1px solid #e5e5e5;}
  .tooltip-label{font-size:14px;font-weight:600;margin-bottom:8px;}
  .tooltip-value{font-size:14px;margin:4px 0;}
`;

// extra colours if new incident types appear
const EXTRA_COLORS = [
  '#32CD32', // green
  '#FFA500', // orange
  '#FFD700', // gold
  '#00BFFF', // blue
  '#8A2BE2', // purple
  '#FF4500', // orange-red
  '#00CED1', // teal
  '#FF69B4', // hot-pink
];

/**
 * Keeps your original styling:
 *  • theftReported ➜ hot-pink thick line  (primary)
 *  • theftDetected & incidentReported ➜ hot-pink light   (secondary)
 *  • any additional incident types ➜ colour-cycled thin lines
 */
const ReportStatistics = ({ employeeId = null, storeId = null }) => {
  const dispatch = useDispatch();
  const { data: rawDaily, loading } = useSelector(
    (state) => state.reports.incidentTypeDaily
  );

  /** Fetch on mount (and when employeeId or storeId changes) */
  useEffect(() => {
    // Create params object with optional employeeId and storeId
    const params = {};
    if (employeeId) params.employee = employeeId;
    if (storeId) params.store_id = storeId;
    
    dispatch(fetchIncidentTypeDailyThunk(params));
  }, [dispatch, employeeId, storeId]);

  /**
   * Transform API { "YYYY-MM-DD": {incidentType: count, ...}, ... }
   * ➜ sorted array for recharts & collect ALL keys.
   */
  const { chartRows, incidentKeys } = useMemo(() => {
    if (!rawDaily) return { chartRows: [], incidentKeys: [] };

    const keys = new Set();
    Object.values(rawDaily).forEach((d) => Object.keys(d).forEach((k) => keys.add(k)));

    const rows = Object.entries(rawDaily)
      .sort(([a], [b]) => new Date(a) - new Date(b))
      .map(([date, counts]) => {
        const row = { date };
        keys.forEach((k) => {
          row[k] = counts[k] ?? 0;
        });
        return row;
      });

    return { chartRows: rows, incidentKeys: Array.from(keys) };
  }, [rawDaily]);

  /** Weekly dashed lines exactly like you had: one per 7th element */
  const weeklyReferenceLines = chartRows
    .filter((_, idx) => idx % 7 === 0)
    .map((row) => (
      <ReferenceLine
        key={row.date}
        x={row.date}
        stroke="#E5E7EB"
        strokeWidth={1}
        opacity={0.5}
        strokeDasharray="3 3"
        segment={[{ y: 0 }, { y: 400 }]}
      />
    ));

  /** Tooltip identical to yours */
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="tooltip-container">
          <p className="tooltip-label">{label}</p>
          {payload.map((e) => (
            <p key={e.name} className="tooltip-value" style={{ color: e.stroke }}>
              {e.name}: {e.value} cases
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  /** Map incident type ➜ line style */
  const renderLine = (type, idx) => {
    // primary & secondary lines keep original colours / widths / opacities
    if (type === 'theftReported') {
      return (
        <Line
          key={type}
          type="monotone"
          dataKey={type}
          name="Theft Reported"
          stroke="#FF1493"
          strokeWidth={3}
          dot={false}
          activeDot={{ r: 8, fill: '#FF1493' }}
        />
      );
    }
    if (type === 'theftDetected' || type === 'incidentReported') {
      return (
        <Line
          key={type}
          type="monotone"
          dataKey={type}
          name={type === 'theftDetected' ? 'Theft Detected' : 'Incident Reported'}
          stroke="#FFB6C1"
          strokeWidth={2}
          dot={false}
          opacity={0.3}
        />
      );
    }
    // any new incident types
    return (
      <Line
        key={type}
        type="monotone"
        dataKey={type}
        name={type}
        stroke={EXTRA_COLORS[idx % EXTRA_COLORS.length]}
        strokeWidth={2}
        dot={false}
        opacity={0.3}
      />
    );
  };

  return (
    <>
      <style>{styles}</style>
      <div className="graph-container">
        <div className="header-container">
          <div className="graph-title">Report Statistics</div>
          <div className="trend-arrow">↗</div>
        </div>

        {loading ? (
          <p>Loading…</p>
        ) : (
          <ResponsiveContainer width="100%" height="85%">
            <LineChart
              data={chartRows}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              {/* gradients (unchanged) */}
              <defs>
                <linearGradient id="theftReportedGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FF1493" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#FFB6C1" stopOpacity={0.3} />
                </linearGradient>

                <linearGradient id="axisGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#FFA500" />
                  <stop offset="25%" stopColor="#32CD32" />
                  <stop offset="50%" stopColor="#FFD700" />
                  <stop offset="75%" stopColor="#FF00FF" />
                  <stop offset="100%" stopColor="#32CD32" />
                </linearGradient>
              </defs>

              {weeklyReferenceLines}

              {/* coloured axis bar (unchanged) */}
              <rect
                x="0"
                y="95%"
                width="100%"
                height="3"
                fill="url(#axisGradient)"
                style={{ opacity: 0.7 }}
              />

              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#666', fontSize: 11 }}
                dy={10}
              />
              <YAxis
                hide
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#666' }}
              />

              <Tooltip content={<CustomTooltip />} />

              {incidentKeys.map(renderLine)}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </>
  );
};

export default ReportStatistics;